/**
 * The native app's sign-in handoff (docs/booking-contracts.md section 7).
 *
 * The app opens a page with ?handoff=<code> on any route, or injects
 * window.__DB_HANDOFF__ at document start. This module is imported right
 * after bootRedirect, so it evaluates before Sentry, analytics or anything
 * that might record the address: the parameter leaves the address bar at
 * once. The code is then redeemed (redeemLiveWebHandoff, 60 seconds, once)
 * for a custom token, and the page signs in with it and carries on at the
 * same route. AuthContext waits for it rather than treating the visitor as
 * signed out, or as whoever the web view was signed in as before (30 Oct:
 * the call page never says Sign in to join while a handoff is signing in).
 * The redeem is tried up to three times over about 12 seconds before the
 * page gives up (31 Oct). A refused or failed handoff leaves the page as
 * it was: the web view's own session when it has one, else signed out.
 *
 * Native's web view keeps nothing (a non-persistent store, decision 83), and
 * the sign-in page showed there after a sign-in that worked (2 Nov). So a
 * handoff session never touches storage for Firebase: auth starts in memory
 * for any load that is a handoff (firebase.ts reads handoffThisLoad). The
 * code is redeemed once; its custom token (good for an hour) is kept for
 * this tab (sessionStorage when it works, else this page's memory), so a
 * second load of the same page, with the spent code or without it, signs in
 * again from the token instead of redeeming. A spent code with a live
 * in-memory session carries on, signed in. The address only ever changes by
 * history.replaceState: never a navigation or a reload.
 *
 * Exactly one redemption per code (3 Nov). The sign-in page showed with
 * "That sign-in link was used": the first try took 2.9 s on the server and
 * succeeded, the page's own 3.5 s timeout dropped its answer, and the retry
 * 0.9 s later spent nothing but was refused as used (05:26 UTC). Not a double
 * mount: the redeem runs at module load, once per document. So each code now
 * has one attempt key (random, kept with the code for the tab), sent with
 * every try; the server gives the same page a token again for the same key
 * inside the 60 seconds. Each code is redeemed through one in-memory promise
 * per document (window.__DB_HANDOFF_REDEEMS__), so a second copy of this
 * module or a second run joins the first rather than sending another redeem.
 * A "used" refusal while a session is live in memory is a success.
 */

declare global {
  interface Window {
    __DB_HANDOFF__?: string; __DB_HANDOFF_STARTED__?: string;
    __DB_HANDOFF_REDEEMS__?: Map<string, Promise<string>>;
  }
}

export const HANDOFF_PARAM = 'handoff';

/**
 * The handoff a page was opened with, and the address without it.
 * @param href The page's address.
 * @param injected The code native injected, if any (preferred).
 * @returns The code, and the address to replace it with (null when the URL had none).
 */
export function takeHandoff(href: string, injected?: string | null): { code: string | null; cleanUrl: string | null } {
  const url = new URL(href);
  const fromUrl = url.searchParams.get(HANDOFF_PARAM);
  if (fromUrl !== null) url.searchParams.delete(HANDOFF_PARAM);
  const code = (injected || fromUrl || '').trim() || null;
  return { code, cleanUrl: fromUrl !== null ? `${url.pathname}${url.search}${url.hash}` : null };
}

let pending: Promise<void> | null = null;
let settled = false;
let outcome = 'no handoff in the address or injected';
let thisLoad = false;
let signedInByHandoff = false;

/** The tab's kept handoff: its code, its custom token, its user, when. */
export interface HandoffSession { code: string; token: string; uid?: string; at: number; attempt?: string }
/** A code being redeemed: its attempt key, kept before the first try. */
export interface HandoffAttempt { code: string; attempt: string; at: number }
/** Where the tab keeps it. */
export interface HandoffStore { get(): string | null; set(v: string): void; clear(): void }

export const SESSION_KEY = 'db_handoff_session';
/** A custom token lasts an hour; one kept longer than this isn't tried. */
export const TOKEN_KEEP_MS = 55 * 60_000;

/**
 * The tab's store: sessionStorage when it takes a write (a non-persistent
 * web view's is in memory and goes with the view; Safari private mode's
 * goes with the tab), else this page's memory.
 * @param storage The window's sessionStorage, if any (a getter that may throw).
 * @returns The store.
 */
export function handoffStore(storage: () => Storage | undefined): HandoffStore {
  try {
    const s = storage();
    if (s) {
      s.setItem(`${SESSION_KEY}_probe`, '1');
      s.removeItem(`${SESSION_KEY}_probe`);
      return {
        get: () => { try { return s.getItem(SESSION_KEY); } catch { return null; } },
        set: (v) => { try { s.setItem(SESSION_KEY, v); } catch { /* full or gone: memory only */ } },
        clear: () => { try { s.removeItem(SESSION_KEY); } catch { /* nothing kept */ } },
      };
    }
  } catch { /* no storage at all */ }
  let kept: string | null = null;
  return { get: () => kept, set: (v) => { kept = v; }, clear: () => { kept = null; } };
}

/**
 * The kept handoff, when it is whole and still fresh.
 * @param raw The stored text.
 * @param now Now (ms).
 * @returns It, or null.
 */
export function readKept(raw: string | null, now: number): HandoffSession | null {
  try {
    const k = raw ? JSON.parse(raw) as HandoffSession : null;
    return k && typeof k.code === 'string' && typeof k.token === 'string' && typeof k.at === 'number' &&
      now - k.at < TOKEN_KEEP_MS ? k : null;
  } catch {
    return null;
  }
}

/**
 * The attempt key for a code: the one the tab kept for it, else a new one.
 * The same page (or a reload of it) sends the same key on every try.
 * @param code The code.
 * @param raw The stored text.
 * @param fresh Makes a new key.
 * @returns The key.
 */
export function attemptFor(code: string, raw: string | null, fresh: () => string): string {
  try {
    const k = raw ? JSON.parse(raw) as Partial<HandoffAttempt> : null;
    if (k && k.code === code && typeof k.attempt === 'string' && /^[A-Za-z0-9_-]{16,100}$/.test(k.attempt)) {
      return k.attempt;
    }
  } catch { /* not ours */ }
  return fresh();
}

/** A new attempt key: 32 random bytes, base64url. */
export function newAttempt(): string {
  const b = new Uint8Array(32);
  crypto.getRandomValues(b);
  return btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Redeems a code once per document: a second call for the same code joins
 * the first call's promise, so no second redeem is ever sent for it.
 * @param code The code.
 * @param redeem The redeem, with its retries.
 * @param redeems The document's redeems (window.__DB_HANDOFF_REDEEMS__).
 * @returns The token.
 */
export function redeemOnce(code: string, redeem: (code: string) => Promise<string>,
  redeems: Map<string, Promise<string>>): Promise<string> {
  const running = redeems.get(code);
  if (running) return running;
  const p = redeem(code);
  redeems.set(code, p);
  // A failure can be tried again by a later run (a new attempt at this code).
  p.catch(() => { if (redeems.get(code) === p) redeems.delete(code); });
  return p;
}

/** Whether an error is the server refusing a code already used. */
export function usedRefusal(err: unknown): boolean {
  return (err as { code?: unknown } | null)?.code === 'functions/failed-precondition';
}

/**
 * What this load does: redeem a new code (once), sign in again from the
 * token the tab kept (the same code loaded again, or a load without one),
 * or nothing.
 * @param code This load's code, if any.
 * @param kept The tab's kept handoff, if fresh.
 * @returns The plan.
 */
export function planHandoff(code: string | null, kept: HandoffSession | null):
  { kind: 'none' } | { kind: 'redeem'; code: string } | { kind: 'reuse'; token: string; code: string } {
  if (kept && (!code || kept.code === code)) return { kind: 'reuse', token: kept.token, code: kept.code };
  if (code) return { kind: 'redeem', code };
  return { kind: 'none' };
}

/** Whether this load is a handoff (a code, or the tab's kept one): auth starts in memory. */
export function handoffThisLoad(): boolean {
  return thisLoad;
}

/** Whether this page's session came from the handoff (the route renders at once). */
export function handoffSignedIn(): boolean {
  return signedInByHandoff;
}

let store: HandoffStore | null = null;
/** Sign-out: the tab forgets its handoff, so a reload doesn't sign back in. */
export function clearHandoffSession(): void {
  store?.clear();
}

/** What a handoff run needs, so a harness can stand in for the page. */
export interface HandoffDeps {
  code: string | null;
  store: HandoffStore;
  now: () => number;
  /** One redeem try, with the code's attempt key. */
  redeem: (code: string, attempt: string) => Promise<string>;
  /** Makes a new attempt key. */
  newAttempt?: () => string;
  /** The document's redeems, so a code is redeemed once. */
  redeems?: Map<string, Promise<string>>;
  signIn: (token: string) => Promise<string>;
  currentUid: () => string | null;
  sleep: (ms: number) => Promise<void>;
}

/**
 * One handoff: redeem once (with its retries) or reuse the kept token, and
 * sign in; a spent code with a live session carries on.
 * @param d The page's parts.
 * @returns What happened, in words, and whether the session is the handoff's.
 */
export async function runHandoff(d: HandoffDeps): Promise<{ outcome: string; signedIn: boolean }> {
  const plan = planHandoff(d.code, readKept(d.store.get(), d.now()));
  if (plan.kind === 'none') return { outcome: 'no handoff in the address or injected', signedIn: false };
  if (plan.kind === 'reuse') {
    try {
      const uid = await d.signIn(plan.token);
      return { outcome: `handoff already redeemed in this tab; signed in again (${uid.slice(0, 6)})`, signedIn: true };
    } catch (err) {
      d.store.clear();
      if (!d.code || d.code === plan.code) {
        return d.currentUid() ? { outcome: 'kept token refused; the live session carries on', signedIn: true } :
          { outcome: `kept token refused (${describeHandoffError(err)})`, signedIn: false };
      }
    }
  }
  const code = d.code as string;
  // The key goes into the tab's store before the first try, so a reload of
  // this page in the middle of it sends the same key.
  const attempt = attemptFor(code, d.store.get(), d.newAttempt ?? newAttempt);
  d.store.set(JSON.stringify({ code, attempt, at: d.now() } satisfies HandoffAttempt));
  try {
    const token = await redeemOnce(code, (c) => redeemWithRetry(() => d.redeem(c, attempt), d.sleep),
      d.redeems ?? new Map());
    d.store.set(JSON.stringify({ code, token, attempt, at: d.now() } satisfies HandoffSession));
    const uid = await d.signIn(token);
    d.store.set(JSON.stringify({ code, token, uid, attempt, at: d.now() } satisfies HandoffSession));
    return { outcome: 'handoff redeemed and signed in', signedIn: true };
  } catch (err) {
    // Spent already, with a session live in memory: that is a success.
    if (d.currentUid()) {
      return {
        outcome: `handoff ${usedRefusal(err) ? 'already redeemed' : 'not redeemed'} (${describeHandoffError(err)}); the live session carries on`,
        signedIn: true,
      };
    }
    return { outcome: `handoff failed (${describeHandoffError(err)})`, signedIn: false };
  }
}

/**
 * Why a page opened without a session is signed out, in one line for the
 * console (31 Oct): what the handoff did.
 * @returns The line.
 */
export function handoffReason(): string {
  return pending !== null && !settled ? 'handoff still signing in' : outcome;
}

/**
 * A failed redeem or sign-in in words: the callable's code and message, or
 * the plain error.
 * @param err The error.
 * @returns The words.
 */
export function describeHandoffError(err: unknown): string {
  const e = err as { code?: unknown; message?: unknown } | null;
  const code = typeof e?.code === 'string' ? e.code : 'error';
  return `${code}: ${String(e?.message ?? err).slice(0, 160)}`;
}

/** Errors worth another try inside the code's 60 seconds. */
const TRANSIENT = new Set(['functions/unavailable', 'functions/internal', 'functions/unknown']);

/**
 * A redeem is tried this many times, each given this long, a pause between:
 * about 16 seconds in all. Each try sends the same attempt key, so a try the
 * server answered after the page gave up on it doesn't spend the code (3 Nov:
 * 5 s per try, from 3.5, since a warm redeem has taken 2.9 s).
 */
export const REDEEM_TRIES = 3;
export const REDEEM_TRY_MS = 5000;
export const REDEEM_PAUSE_MS = 750;

/**
 * Whether a failed redeem is tried again: the server unavailable, an
 * internal error, the network, or the page's own timeout (31 Oct: it gave
 * up before a 3 second cold start answered). The server's own expired is
 * deadline-exceeded too, with its own words, and is not.
 * @param err The error.
 * @returns Retry.
 */
export function retriesHandoff(err: unknown): boolean {
  const e = err as { code?: unknown; message?: unknown } | null;
  const code = String(e?.code ?? '');
  return TRANSIENT.has(code) || (code === 'functions/deadline-exceeded' && e?.message === 'deadline-exceeded');
}

/**
 * Redeems with up to REDEEM_TRIES tries; a refusal ends it at once.
 * @param attempt One try.
 * @param sleep Waits between tries.
 * @returns The first answer.
 */
export async function redeemWithRetry<T>(attempt: () => Promise<T>, sleep: (ms: number) => Promise<void>): Promise<T> {
  for (let i = 1; ; i++) {
    try {
      return await attempt();
    } catch (err) {
      if (i >= REDEEM_TRIES || !retriesHandoff(err)) throw err;
      await sleep(REDEEM_PAUSE_MS);
    }
  }
}

/**
 * Once the handoff is done: whether an auth callback for one user is
 * superseded, because the handoff signed someone else in (whose own
 * callback carries on). Same user, or the handoff failed: it carries on.
 * @param callbackUid The callback's user, or null.
 * @param currentUid Who is signed in now, or null.
 * @returns Superseded.
 */
export function supersededByHandoff(callbackUid: string | null, currentUid: string | null): boolean {
  return callbackUid !== currentUid;
}

function start(): void {
  if (typeof window === 'undefined') return;
  const injected = window.__DB_HANDOFF__ ?? null;
  const { code, cleanUrl } = takeHandoff(window.location.href, injected);
  // Only ever replaceState: never a navigation or a reload.
  if (cleanUrl !== null) window.history.replaceState(window.history.state, '', cleanUrl);
  if (injected) delete window.__DB_HANDOFF__;
  store = handoffStore(() => window.sessionStorage);
  const kept = readKept(store.get(), Date.now());
  if (!code && !kept) return;
  // This document runs a code once, however often the module is reached.
  const key = code ?? `kept:${kept?.code}`;
  if (window.__DB_HANDOFF_STARTED__ === key) return;
  window.__DB_HANDOFF_STARTED__ = key;
  thisLoad = true;
  outcome = 'handoff found, not finished';
  const tabStore = store;
  pending = (async () => {
    try {
      const [{ auth, functions }, { signInWithCustomToken }, { httpsCallable }] = await Promise.all([
        import('./firebase'), import('firebase/auth'), import('firebase/functions'),
      ]);
      const call = httpsCallable<{ handoff: string; attempt: string }, { token: string }>(functions,
        'redeemLiveWebHandoff', { timeout: REDEEM_TRY_MS });
      window.__DB_HANDOFF_REDEEMS__ ??= new Map();
      const r = await runHandoff({
        code, store: tabStore, now: Date.now, redeems: window.__DB_HANDOFF_REDEEMS__,
        redeem: async (c, attempt) => (await call({ handoff: c, attempt })).data.token,
        signIn: async (t) => (await signInWithCustomToken(auth, t)).user.uid,
        currentUid: () => auth.currentUser?.uid ?? null,
        sleep: (ms) => new Promise((ok) => setTimeout(ok, ms)),
      });
      outcome = r.outcome;
      signedInByHandoff = r.signedIn;
      if (!r.signedIn) console.warn('[handoff] sign-in handoff refused:', r.outcome);
    } catch (err) {
      outcome = `handoff failed (${describeHandoffError(err)})`;
      console.warn('[handoff] sign-in handoff refused', err);
    } finally {
      settled = true;
    }
  })();
}

start();

/** Whether a handoff is still signing in. */
export function handoffPending(): boolean {
  return pending !== null && !settled;
}

/** Resolves once the handoff is done, signed in or not. */
export function handoffSettled(): Promise<void> {
  return pending ?? Promise.resolve();
}
